import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArticleForm } from '@/components/article/ArticleForm';
import { useArticles } from '@/hooks/useArticles';
import { ArrowLeft, FileText, Calendar } from 'lucide-react';
import { ArticleFormData } from '@/types/article';
import { toast } from 'sonner';
import { InternalLinksPreview } from '@/components/article/InternalLinksPreview';
import { Alert, AlertDescription } from '@/components/ui/alert';

const NewArticle: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { createArticle, updateArticle, categories, tags, authors, loading, fetchCategories, fetchTags, fetchAuthors } = useArticles();
  
  // Get prefilled data from seasonal content planner
  const prefilledData = location.state?.prefilledData;
  
  // For seasonal topics, we want to use the AI builder, not pre-fill the form
  const seasonalTopicForAI = prefilledData ? {
    title: prefilledData.title,
    description: prefilledData.excerpt,
    keywords: prefilledData.keywords || []
  } : null;

  const handleSubmit = async (data: ArticleFormData) => {
    try {
      const articleData = data as any;
      console.log('📤 Submitting article...', { 
        title: data.title, 
        status: data.status, 
        hasId: !!articleData.id,
        id: articleData.id,
        tag_ids: data.tag_ids,
        tag_count: data.tag_ids?.length 
      });
      
      // Check if this is updating an existing draft or creating new
      if (articleData.id) {
        // Update existing draft
        const { id, ...updateData } = articleData;
        console.log('📝 Updating article:', id);
        console.log('📝 Update data:', {
          status: updateData.status,
          tag_ids: updateData.tag_ids,
          tag_count: updateData.tag_ids?.length,
          title: updateData.title
        });
        
        const result = await updateArticle(id, updateData);
        console.log('✅ Update successful:', result);
        
        if (updateData.status === 'published') {
          toast.success('Article published successfully!');
          // Open published article in new window
          if (result?.slug) {
            const articleUrl = `${window.location.origin}/articles/${result.slug}`;
            window.open(articleUrl, '_blank');
          }
          // Navigate to articles list
          navigate('/admin/articles');
          return result;
        } else {
          toast.success('Article saved successfully!');
          navigate('/admin/articles');
        }
      } else {
        // Create new article
        console.log('✨ Creating new article with status:', data.status);
        console.log('🏷️ Tag IDs being sent to createArticle:', data.tag_ids);
        const result = await createArticle(data);
        
        if (data.status === 'published') {
          toast.success('Article published successfully!');
          // Open published article in new window
          if (result?.slug) {
            const articleUrl = `${window.location.origin}/articles/${result.slug}`;
            window.open(articleUrl, '_blank');
          }
          // Navigate to articles list
          navigate('/admin/articles');
          return result;
        } else {
          toast.success('Article created successfully!');
          navigate('/admin/articles');
          return result;
        }
      }
    } catch (error: any) {
      console.error('❌ Error saving article:', error);
      console.error('❌ Error details:', JSON.stringify(error, null, 2));
      const errorMessage = error?.message || error?.error?.message || (typeof error === 'string' ? error : 'Unknown error');
      toast.error(`Failed to save article: ${errorMessage}`);
      throw error; // Re-throw so ArticleForm can handle it
    }
  };

  const handleCancel = () => {
    navigate('/admin/articles');
  };

  const handleCategoriesUpdate = () => {
    fetchCategories();
  };

  const handleTagsUpdate = () => {
    fetchTags();
  };

  const handleAuthorsUpdate = async () => {
    await fetchAuthors();
  };

  return (
    <div className="container mx-auto py-8 px-4 font-geist">
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/admin/articles')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Articles
        </Button>
      </div>

      {seasonalTopicForAI && (
        <Alert className="mb-6">
          <Calendar className="h-4 w-4" />
          <AlertDescription>
            Ready to generate article from seasonal content suggestion: "{seasonalTopicForAI.title}"
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Create New Article
          </CardTitle>
          <CardDescription>
            Use the AI Article Builder to generate content with OpenAI, or write and publish a new blog post manually.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ArticleForm
            seasonalTopic={seasonalTopicForAI}
            categories={categories}
            tags={tags}
            authors={authors}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={loading}
            onCategoriesUpdate={handleCategoriesUpdate}
            onTagsUpdate={handleTagsUpdate}
            onAuthorsUpdate={handleAuthorsUpdate}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default NewArticle;